import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2, ShoppingBag, AlertTriangle } from 'lucide-react';

/*
 * CheckoutConsentModal — a required "I have read and agree" gate that sits
 * between the Buy Now click and the Stripe redirect. UK consumer law
 * doesn't strictly require a tick-box before checkout, but showing the
 * ToS + Conditions of Sale up-front (and asking for an explicit consent)
 * is cleanest for our audit trail and best-practice for age-restricted
 * items like the Field Knife.
 *
 * Props:
 *   product       — { name, price, slug, disclaimer? } required.
 *   busy          — bool, true while the Stripe session is being minted.
 *   onConfirm     — callback fired when user ticks the box and clicks
 *                   "Continue to payment".
 *   onCancel      — callback fired when the modal is dismissed.
 */
export default function CheckoutConsentModal({ product, busy, onConfirm, onCancel }) {
  const [agreed, setAgreed] = useState(false);

  // Close on Escape.
  useEffect(() => {
    if (!product) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [product, busy, onCancel]);

  if (!product) return null;

  return (
    <div
      data-testid="checkout-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-consent-title"
      className="fixed inset-0 z-[100] grid place-items-center bg-black/60 backdrop-blur-sm px-4 py-8"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
    >
      <div className="relative max-w-lg w-full bg-[var(--brand-parchment)] border-2 border-[var(--brand-forest-deep)]/15 border-t-8 border-t-[var(--brand-gold)] shadow-2xl">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center text-[var(--brand-forest-deep)] hover:bg-[var(--brand-forest-deep)]/10 disabled:opacity-40"
        >
          <X size={16} />
        </button>

        <div className="px-6 pt-8 pb-6 sm:px-8">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--brand-forest-deep)]">
            Review before checkout
          </div>
          <h2
            id="checkout-consent-title"
            className="mt-2 font-display text-2xl sm:text-3xl text-[var(--brand-forest-deep)] leading-tight"
          >
            {product.name}
          </h2>
          <div className="mt-1 text-sm text-[var(--brand-olive)] font-semibold">
            {product.price}
          </div>

          <div
            data-testid="checkout-consent-free-delivery"
            className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)] bg-[var(--brand-gold)]/20 px-3 py-1.5"
          >
            <ShoppingBag size={12} strokeWidth={2.4} />
            Free UK delivery on all orders
          </div>

          {product.disclaimer && (
            <div
              data-testid="checkout-consent-disclaimer"
              className="mt-5 flex items-start gap-3 bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] px-4 py-3"
            >
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[var(--brand-forest-deep)]" strokeWidth={2.25} />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--brand-forest-deep)]">
                  {product.disclaimer.label}
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-[var(--brand-ink)]">
                  {product.disclaimer.text}
                </p>
              </div>
            </div>
          )}

          <label
            data-testid="checkout-consent-checkbox-wrap"
            className="mt-6 flex items-start gap-3 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              data-testid="checkout-consent-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[var(--brand-forest-deep)] shrink-0"
            />
            <span className="text-sm leading-relaxed text-[var(--brand-ink)]">
              I have read and agree to the{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="link-expand font-semibold text-[var(--brand-forest-deep)]"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/conditions-of-sale"
                target="_blank"
                rel="noopener noreferrer"
                className="link-expand font-semibold text-[var(--brand-forest-deep)]"
              >
                Conditions of Sale
              </Link>
              , including the{' '}
              <Link
                to="/disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className="link-expand font-semibold text-[var(--brand-forest-deep)]"
              >
                Disclaimer
              </Link>
              .
              {product.disclaimer?.label?.toLowerCase().includes('18') && (
                <span className="block mt-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--brand-oak)]">
                  I confirm I am 18 years of age or older.
                </span>
              )}
            </span>
          </label>

          <div className="mt-7 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              data-testid="checkout-consent-cancel"
              className="px-5 py-3 text-sm font-semibold text-[var(--brand-forest-deep)] hover:bg-[var(--brand-forest-deep)]/10 disabled:opacity-40"
            >
              Not just now
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!agreed || busy}
              data-testid="checkout-consent-confirm"
              className="btn-gold px-6 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Starting&hellip;
                </>
              ) : (
                <>
                  <ShoppingBag size={14} />
                  Continue to payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
